/**
 * How many people could not open a draft as requested, said the way the author reads it
 * (LO-211), without React or a network, so each sentence can be read and tested on its own.
 *
 * A person opens an app only if they hold every tag its manifest reads or writes with.
 * The kernel can say, for a draft and before anybody is asked to approve it, how many
 * people in the author's organisation would be shown the missing-permissions screen
 * instead: `GET /manifest-requests/{id}/access-impact`,
 * `{total_users, blocked_count, blocked: [{user, missing: [{type, tag, access}]}]}`,
 * computed against the request's proposed manifest.
 *
 * ## What this is, and what it is not
 *
 * **Information, never a block.** Nothing here holds Send for approval back: the number is for
 * the author to know before they send, and what to do about it (ask for less access, or split
 * the app) is theirs to choose.
 *
 * **The kernel decides who is blocked.** The count is the kernel's `blocked_count`, and what
 * people lack is the kernel's own `missing`, said as it said it. Nothing here works out who lacks
 * what from a list of tags, and a name the kernel did not give is not made up: it names people
 * only when its answer does, which is the kernel's way of saying the author may see names.
 *
 * **It says what it does not prove, on the figure.** The basis is one short line that goes with
 * the sentence: counted now, from tags today, and not a promise about people added later.
 *
 * The sentence has a twin in `mcp/lopie_mcp/impact.py`, which the Builder agent says to the
 * author in the chat. Both are tested against the same list of sentences.
 */

/** What the number is of, and what it does not cover: short enough to say beside it. */
export const IMPACT_BASIS = "Counted now, from each person's tags today, against what this draft asks for, among the people who may use the app. People added later, or whose tags change, aren't in it.";

/** When the kernel cut its list of people short (`truncated`), the number is a floor, and says so. */
export const TRUNCATED_LINE = "The Kernel's list of people was cut short, so only the first part was counted and the real number may be higher.";

/** What widens who can use the app, and that knowing it holds nothing back. */
export const WIDEN = "Asking the builder for less access, or splitting the app in two, is how to widen who can use it. Knowing this doesn't stop it being sent: that is up to you.";

/** The most names listed before "and N more". */
export const NAMES_SHOWN = 50;

const text = (v) => String(v ?? "").trim();

/** `finance (read Payment)`: the tag, and what it is needed for. */
export function wordsOf(item) {
  const access = text(item?.access);
  const kind = text(item?.type);
  return `${text(item?.tag)} (${access ? `${access} ${kind}` : kind})`;
}

function itemOf(raw) {
  if (!raw || typeof raw !== "object") return null;
  const type = text(raw.type);
  const tag = text(raw.tag);
  if (!type || !tag) return null;
  return { type, tag, access: text(raw.access) };
}

/** What the blocked people are missing, each item with how many of them miss it: the most common
 *  first and then by tag, so two readings of one answer agree. */
export function lackingOf(blocked) {
  const counts = new Map();
  for (const entry of Array.isArray(blocked) ? blocked : []) {
    if (!entry || typeof entry !== "object") continue;
    const mine = new Map();
    for (const raw of Array.isArray(entry.missing) ? entry.missing : []) {
      const item = itemOf(raw);
      if (item) mine.set(`${item.type}\u0000${item.tag}\u0000${item.access}`, item);
    }
    for (const [key, item] of mine) {
      const held = counts.get(key);
      counts.set(key, { ...item, people: (held?.people || 0) + 1 });
    }
  }
  return [...counts.values()].sort((a, b) =>
    b.people - a.people || a.tag.localeCompare(b.tag) || a.type.localeCompare(b.type) || a.access.localeCompare(b.access));
}

function nameOf(user) {
  if (user && typeof user === "object") {
    for (const key of ["name", "display_name", "email", "username", "id"]) {
      if (text(user[key])) return text(user[key]);
    }
    return "";
  }
  return text(user);
}

/** Who the kernel named, with what each lacks, or null when it named nobody. The kernel decides
 *  whom the author may see by whom it names, so a list with no `user` in it is a list the author
 *  is not shown names from, and none is made up. */
export function namesOf(answer) {
  const blocked = Array.isArray(answer?.blocked) ? answer.blocked : [];
  const named = blocked
    .filter((b) => b && typeof b === "object")
    .map((b) => ({
      name: nameOf(b.user),
      lacks: (Array.isArray(b.missing) ? b.missing : []).map(itemOf).filter(Boolean).map(wordsOf),
    }))
    .filter((n) => n.name);
  return named.length ? named : null;
}

const count = (v) => (Number.isInteger(v) && v >= 0 ? v : null);

/** The one sentence the author reads, or "" for an answer with no counts. */
export function sentenceOf(answer) {
  if (!answer || typeof answer !== "object" || Array.isArray(answer)) return "";
  const total = count(answer.total_users);
  const blocked = count(answer.blocked_count);
  if (total === null || blocked === null) return "";
  if (blocked === 0) {
    if (total === 0) return "The Kernel counted no people in your org, so there is nobody it could say could not open it.";
    if (total === 1) return "The one person in your org could open it as requested.";
    return `Everyone in your org (${total} people) could open it as requested.`;
  }
  const who = total === 1 ? "the one person" : `${blocked} of ${total} people`;
  const base = `With this access, ${who} in your org couldn't open it as requested`;
  const items = lackingOf(answer.blocked);
  if (!items.length) return `${base}. The Kernel didn't say what they lack.`;
  if (items.length === 1) return `${base}: they lack ${wordsOf(items[0])}.`;
  const parts = items.map((i) => `${wordsOf(i)} (${i.people === blocked ? "all of them" : `${i.people} of them`})`);
  return `${base}: they lack ${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}.`;
}

/**
 * What the page draws, from the kernel's answer. `kind` is `blocked`, `everyone` or `unreadable`
 * (an answer with no counts in it is never worded as a number).
 */
export function impactOf(answer) {
  const sentence = sentenceOf(answer);
  if (!sentence) {
    return {
      kind: "unreadable", sentence: "", total: null, blocked: null, lacking: [], names: null, widen: "", basis: "",
      truncated: false, truncatedLine: "", kernelSays: [],
    };
  }
  const total = answer.total_users;
  const blocked = answer.blocked_count;
  const lacking = lackingOf(answer.blocked).map((i) => ({ ...i, words: wordsOf(i), all: i.people === blocked }));
  const names = namesOf(answer);
  return {
    kind: blocked ? "blocked" : "everyone",
    sentence, total, blocked, lacking,
    names: names && names.length > NAMES_SHOWN
      ? [...names.slice(0, NAMES_SHOWN), { name: `and ${names.length - NAMES_SHOWN} more`, lacks: [], more: true }]
      : names,
    widen: blocked ? WIDEN : "",
    basis: IMPACT_BASIS,
    truncated: answer.truncated === true,
    truncatedLine: answer.truncated === true ? TRUNCATED_LINE : "",
    // The kernel's own sentences about what the count does not prove, as it sent them: one click in.
    kernelSays: (Array.isArray(answer.does_not_prove) ? answer.does_not_prove : []).filter((x) => typeof x === "string"),
  };
}

/** What to put in the change box when the author wants fewer people locked out: the number, in
 *  the words it was said in, and the two ways to widen who can use the app. */
export function askForLessRequest(view) {
  return [
    "Please change the access this app asks for so more people in my org can open it.",
    view?.sentence || "",
    "Ask for less access wherever the app can do its job with it, or split the app in two so the part that needs the tag is its own app. Tell me what you would change before you save the new draft.",
  ].filter(Boolean).join("\n");
}
